import {
  createRole,
  searchRoles,
  getRole,
  updateRole,
  deleteRole as apiDeleteRole,
  searchUsersInRole,
  listRolesForUser as apiListRolesForUser,
  getOwnRoles as apiGetOwnRoles,
  assignUsersToRole,
  removeUsersFromRole,
  searchActiveOrganisationUsers,
  type PermissionRoleInDb,
  type PermissionRoleCreate,
  type PermissionRoleUpdate,
  type FilterableSearchQuery,
  type SearchQuery,
  type UserInDb,
  type UserSearchResult,
  type RoleUsersResult,
} from '@/api';

/**
 * Extract error message from various error types
 */
function extractErrorMessage(err: unknown, defaultMessage: string): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'object' && err !== null && 'detail' in err) {
    const detail = err.detail;
    return String(detail);
  }
  return defaultMessage;
}

export interface RolesSearchParams {
  text?: string;
  page?: number;
  pageSize?: number;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface UsersSearchParams {
  text?: string;
  page?: number;
  pageSize?: number;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
}

/**
 * The two built-in roles. The backend has no flag that tells them apart, so
 * like the backend they are matched by `protected` plus the exact name.
 *
 * - Organisation Member: everyone in the organisation holds it (it is granted
 *   on joining). Its description and permissions can change, not its name.
 * - Organisation Admin: its holders bypass every permission check, so only its
 *   description can change.
 *
 * Neither can be renamed or deleted.
 */
export const ORGANISATION_MEMBER_ROLE_NAME = 'Organisation Member';
export const ORGANISATION_ADMIN_ROLE_NAME = 'Organisation Admin';

type BuiltInRoleCandidate = Pick<PermissionRoleInDb, 'protected' | 'name'>;

/** The built-in role every member holds. */
export function isMemberRole(role: BuiltInRoleCandidate): boolean {
  return role.protected && role.name === ORGANISATION_MEMBER_ROLE_NAME;
}

/** The built-in role whose holders bypass every permission check. */
export function isAdminRole(role: BuiltInRoleCandidate): boolean {
  return role.protected && role.name === ORGANISATION_ADMIN_ROLE_NAME;
}

export const PermissionsService = {
  /**
   * Search roles with pagination and filtering
   */
  async searchRoles(
    params: RolesSearchParams = {}
  ): Promise<{ results: PermissionRoleInDb[]; result_count: number }> {
    try {
      const query: FilterableSearchQuery = {
        text: params.text ?? '',
        limit: params.pageSize ?? 10,
        offset: params.page ? (params.page - 1) * (params.pageSize ?? 10) : 0,
        sort_key: params.sortKey ?? 'created_at',
        sort_direction: params.sortDirection ?? 'desc',
        similarity_threshold: 0.5,
        column_filters: {},
      };

      const response = await searchRoles({ body: query });

      if (!response.data) {
        throw new Error('Failed to search roles');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to search roles. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Fetch a single role by UID
   */
  async getRole(uid: string): Promise<PermissionRoleInDb> {
    try {
      const response = await getRole({ path: { uid } });

      if (!response.data) {
        throw new Error('Failed to fetch role');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to fetch role. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Create a new role
   */
  async createRole(data: PermissionRoleCreate): Promise<PermissionRoleInDb> {
    try {
      const response = await createRole({ body: data });

      if (!response.data) {
        throw new Error('Failed to create role');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to create role. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Update an existing role
   */
  async updateRole(uid: string, data: PermissionRoleUpdate): Promise<PermissionRoleInDb> {
    try {
      const response = await updateRole({ path: { uid }, body: data });

      if (!response.data) {
        throw new Error('Failed to update role');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to update role. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Delete a role
   */
  async deleteRole(uid: string): Promise<void> {
    try {
      await apiDeleteRole({ path: { uid } });
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to delete role. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Get roles for the current user (profile/me). Uses GET /user/me/roles.
   */
  async getOwnRoles(): Promise<PermissionRoleInDb[]> {
    try {
      const response = await apiGetOwnRoles();
      if (response.error)
        throw new Error(
          extractErrorMessage(response.error, 'Failed to load your roles. Please try again.')
        );
      const data = response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to load your roles. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * List roles assigned to a user (admin viewing another user). Uses GET /user/{uid}/roles.
   */
  async listRolesForUser(userUid: string): Promise<PermissionRoleInDb[]> {
    try {
      const response = await apiListRolesForUser({ path: { user_uid: userUid } });

      if (!response.data) {
        throw new Error('Failed to list roles for user');
      }

      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to list roles for user. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Search the users assigned to a role, one page at a time.
   */
  async searchUsersInRole(
    roleUid: string,
    params: UsersSearchParams = {}
  ): Promise<UserSearchResult> {
    try {
      const pageSize = params.pageSize ?? 10;
      const query: SearchQuery = {
        text: params.text ?? '',
        limit: pageSize,
        offset: params.page ? (params.page - 1) * pageSize : 0,
        sort_key: params.sortKey ?? 'email',
        sort_direction: params.sortDirection ?? 'asc',
        similarity_threshold: 0.5,
      };

      const response = await searchUsersInRole({ path: { role_uid: roleUid }, body: query });

      if (!response.data) {
        throw new Error('Failed to list users in role');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to list users in role. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Every user uid in a role, paging through the search at the backend's cap of
   * 50 per request.
   * ponytail: one request per 50 members; fine for role sizes we see, a
   * membership-aware user search would replace it if roles grow into thousands.
   */
  async listAllUserUidsInRole(roleUid: string): Promise<string[]> {
    const pageSize = 50;
    const uids: string[] = [];
    for (let page = 1; ; page++) {
      const { results, result_count } = await this.searchUsersInRole(roleUid, { page, pageSize });
      uids.push(...results.map(user => user.uid));
      if (results.length < pageSize || uids.length >= result_count) return uids;
    }
  },

  /**
   * Assign one or more users to a role (single endpoint for both single and bulk).
   */
  async assignUserToRole(roleUid: string, userUid: string): Promise<RoleUsersResult> {
    return this.assignUsersToRole(roleUid, [userUid]);
  },

  /**
   * Remove one or more users from a role (single endpoint for both single and bulk).
   */
  async removeUserFromRole(roleUid: string, userUid: string): Promise<void> {
    await this.removeUsersFromRole(roleUid, [userUid]);
  },

  /**
   * Assign users to a role (handles both single and bulk via same endpoint).
   */
  async assignUsersToRole(roleUid: string, userUids: string[]): Promise<RoleUsersResult> {
    try {
      const response = await assignUsersToRole({
        path: { role_uid: roleUid },
        body: { user_uids: userUids },
      });

      if (!response.data) {
        throw new Error('Failed to assign users to role');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to assign users to role. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Remove users from a role (handles both single and bulk via same endpoint).
   */
  async removeUsersFromRole(roleUid: string, userUids: string[]): Promise<RoleUsersResult> {
    try {
      const response = await removeUsersFromRole({
        path: { role_uid: roleUid },
        body: { user_uids: userUids },
      });

      if (!response.data) {
        throw new Error('Failed to remove users from role');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to remove users from role. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Bulk assign users to a role (uses same endpoint as single assign).
   */
  async bulkAssignUsersToRole(roleUid: string, userUids: string[]): Promise<RoleUsersResult> {
    return this.assignUsersToRole(roleUid, userUids);
  },

  /**
   * Bulk remove users from a role (uses same endpoint as single remove).
   */
  async bulkRemoveUsersFromRole(roleUid: string, userUids: string[]): Promise<RoleUsersResult> {
    return this.removeUsersFromRole(roleUid, userUids);
  },

  /**
   * Search users (for assign-to-role modal). Uses active organisation users search endpoint.
   */
  async searchUsers(
    params: UsersSearchParams = {}
  ): Promise<{ results: UserInDb[]; result_count: number }> {
    try {
      const query: SearchQuery = {
        text: params.text ?? '',
        limit: params.pageSize ?? 20,
        offset: params.page ? (params.page - 1) * (params.pageSize ?? 20) : 0,
        sort_key: params.sortKey ?? 'email',
        sort_direction: params.sortDirection ?? 'asc',
        similarity_threshold: 0.5,
      };

      const apiResponse = await searchActiveOrganisationUsers({ body: query });
      if (apiResponse.error) {
        throw new Error(
          extractErrorMessage(
            apiResponse.error,
            'Failed to fetch organisation users. Please try again.'
          )
        );
      }

      return apiResponse.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch organisation users. Please try again.'),
        { cause: error }
      );
    }
  },
};
